import PropTypes from "prop-types";

import {
  Document,
  Font,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import { formatDateTime, formatMoney, formatNumber } from "@/features/shared";
import { OutletRoleEnum } from "@/features/outlets";

Font.registerHyphenationCallback((word) => {
  // Return entire word as unique part
  return [word];
});

const getLastPartAfterDash = (str) => {
  if (!str) return "";
  const parts = str.split("-");
  return parts[parts.length - 1];
};

const styles = StyleSheet.create({
  page: {
    padding: 20,
    fontSize: 7,
    fontFamily: "Helvetica",
    color: "#000000",
    lineHeight: 1.25,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 20,
    right: 20,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 6,
  },
  section: {
    marginTop: 16,
  },
  container: {
    flexDirection: "row",
    width: "100%",
  },
  headerLeftSection: {
    flex: 2,
    flexDirection: "row",
    alignItems: "flex-start",
  },
  headerRightSection: {
    flex: 1,
  },
  headerLogo: {
    width: "15%", // Relative image width
    height: "auto", // Maintain aspect ratio
  },
  flexibleBox: {
    flex: 1,
    paddingHorizontal: 10,
  },
  socialMediaSection: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 1,
  },
  socialLogo: {
    width: 10, // Size of the logos
    height: 10, // Keep it square to maintain aspect ratio
    marginRight: 2,
  },
  paymentMethod: {
    fontSize: 8,
    marginBottom: 2,
  },
  productTitle: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
    marginBottom: 8,
  },
  tableHeader: {
    flexDirection: "row",
    fontFamily: "Helvetica-Bold",
    backgroundColor: "#ececec",
    color: "#000",
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableRow: {
    flexDirection: "row",
    borderColor: "#ececec",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  tableCell: {
    paddingLeft: 2,
    paddingRight: 2,
    paddingTop: 1,
  },
  priceSummaryRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 4,
  },
  priceSummaryValue: {
    width: "40%",
    textAlign: "right",
  },
  priceSummaryGrandTotal: {
    fontFamily: "Helvetica-Bold",
    color: "#364F6B",
  },
});

export const SalesOrderPdf = ({ salesOrder }) => {
  const isRetail = salesOrder.seller.role == OutletRoleEnum.RETAIL;

  return (
    <Document>
      <Page size="A5" style={styles.page}>
        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Hal. ${pageNumber} dari ${totalPages}`
            }
          />
          <Text style={{ fontFamily: "Helvetica-Oblique" }}>
            Dicetak: {formatDateTime(new Date())}
          </Text>
        </View>

        <View style={styles.container}>
          {/* Left Section with Fixed and Flexible Boxes */}
          <View style={styles.headerLeftSection}>
            <Image style={styles.headerLogo} src="/logo.png" />
            <View style={styles.flexibleBox}>
              <Text>Optik 150K - {salesOrder.seller?.name || "Johor"}</Text>
              <Text wrap={false}>{salesOrder.seller?.address || ""}</Text>
              <Text>www.optik150k.com</Text>
            </View>
          </View>

          {/* Right Section */}
          <View style={styles.headerRightSection}>
            <Text
              style={{
                fontSize: 9,
                fontFamily: "Helvetica-Bold",
                color: "#364F6B",
                marginBottom: 2,
              }}
            >
              NOTA PEMBELIAN
            </Text>
            <Text>No: {getLastPartAfterDash(salesOrder.referenceNumber)}</Text>
            <Text>
              Waktu Transaksi: {formatDateTime(salesOrder.transactionDate)}
            </Text>
            <Text>Kasir: {salesOrder.creator?.name}</Text>
            <Text>Pelanggan: {salesOrder.buyer?.name}</Text>
          </View>
        </View>

        {/* General Products Table */}
        {salesOrder.salesOrderGeneralProductVariants?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.productTitle}>Produk Umum:</Text>

            <View style={styles.tableHeader}>
              <Text style={[styles.tableCell, { width: "50%" }]}>
                Nama Barang
              </Text>
              <Text
                style={[styles.tableCell, { width: "5%", textAlign: "right" }]}
              >
                Qty
              </Text>
              <Text
                style={[styles.tableCell, { width: "15%", textAlign: "right" }]}
              >
                @Harga (Rp)
              </Text>
              <Text
                style={[styles.tableCell, { width: "15%", textAlign: "right" }]}
              >
                Diskon (Rp)
              </Text>
              <Text
                style={[styles.tableCell, { width: "15%", textAlign: "right" }]}
              >
                Total (Rp)
              </Text>
            </View>

            {salesOrder.salesOrderGeneralProductVariants.map((product) => (
              <View key={product.id} style={styles.tableRow}>
                <Text style={[styles.tableCell, { width: "50%" }]}>
                  {product.name}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: "5%", textAlign: "right" },
                  ]}
                >
                  {product.quantity}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: "15%", textAlign: "right" },
                  ]}
                >
                  {formatNumber(product.price)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: "15%", textAlign: "right" },
                  ]}
                >
                  {formatNumber(product.discount)}
                </Text>
                <Text
                  style={[
                    styles.tableCell,
                    { width: "15%", textAlign: "right" },
                  ]}
                >
                  {formatNumber(
                    product.quantity * product.price - product.discount
                  )}
                </Text>
              </View>
            ))}
          </View>
        )}

        {/* Lens Products Table */}
        {salesOrder.salesOrderLensProducts?.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.productTitle}>Produk Lensa:</Text>
            {salesOrder.salesOrderLensProducts.map((lensProduct, index) => (
              <View
                key={lensProduct.id}
                wrap={false}
                style={{ marginTop: index === 0 ? 0 : 12 }}
              >
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    marginBottom: 4,
                  }}
                >
                  <Text>
                    {index + 1}.{" "}
                    {lensProduct.lensType?.name || lensProduct.name}
                  </Text>
                  <Text>
                    {formatMoney(lensProduct.price - lensProduct.discount)}
                  </Text>
                </View>

                <View style={styles.tableHeader}>
                  <Text style={[styles.tableCell, { flex: 1 }]}>Sisi</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>Sph</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>Cyl</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>Axis</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>PD</Text>
                  <Text style={[styles.tableCell, { flex: 1 }]}>Prism</Text>
                </View>

                {lensProduct.salesOrderLensProductVariants?.map((variant) => {
                  let parsedNote = {};
                  try {
                    parsedNote = JSON.parse(variant.note || "{}");
                  } catch {
                    parsedNote = {};
                  }
                  return (
                    <View key={variant.id} style={styles.tableRow}>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {variant.side}
                      </Text>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {variant.variantAttribute1Value || "0"}
                      </Text>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {variant.variantAttribute2Value || "0"}
                      </Text>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {parsedNote.axis || "-"}
                      </Text>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {parsedNote.pd || "-"}
                      </Text>
                      <Text style={[styles.tableCell, { flex: 1 }]}>
                        {parsedNote.prism || "-"}
                      </Text>
                    </View>
                  );
                })}

                {lensProduct.note && (
                  <Text style={{ marginTop: 2, fontSize: 6 }}>
                    {"Note: " + lensProduct.note}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        <View wrap={false} style={[styles.section, { flexDirection: "row" }]}>
          <View style={{ flex: 1 }}>
            {isRetail ? (
              <>
                <Text style={styles.paymentMethod}>Metode Pembayaran:</Text>
                {salesOrder.salesOrderPayments.map((payment) => (
                  <Text key={payment.id} style={[styles.paymentMethod]}>
                    - {payment.paymentMethod?.name} (
                    {formatMoney(payment.paymentAmount)})
                  </Text>
                ))}
              </>
            ) : (
              <>
                <Text style={styles.paymentMethod}>Syarat Pembayaran:</Text>
                <Text style={[styles.paymentMethod]}>
                  - {salesOrder.paymentTerm.name}
                </Text>
              </>
            )}
          </View>

          <View style={{ flex: 1 }}>
            <View style={styles.priceSummaryRow}>
              <Text>Subtotal:</Text>
              <Text style={styles.priceSummaryValue}>
                {formatMoney(
                  salesOrder.subtotal ||
                    (salesOrder.salesOrderGeneralProductVariants?.reduce(
                      (sum, variant) =>
                        sum +
                        variant.quantity * variant.price -
                        variant.discount,
                      0
                    ) || 0) +
                      (salesOrder.salesOrderLensProducts?.reduce(
                        (sum, product) =>
                          sum + product.price - product.discount,
                        0
                      ) || 0)
                )}
              </Text>
            </View>
            <View style={styles.priceSummaryRow}>
              <Text>Total Diskon:</Text>
              <Text style={styles.priceSummaryValue}>
                {formatMoney(
                  salesOrder.totalDiscount ??
                    (salesOrder.salesOrderGeneralProductVariants?.reduce(
                      (sum, variant) => sum + variant.discount,
                      0
                    ) || 0) +
                      (salesOrder.salesOrderLensProducts?.reduce(
                        (sum, product) => sum + product.discount,
                        0
                      ) || 0)
                )}
              </Text>
            </View>
            <View style={[styles.priceSummaryRow, { fontSize: 9 }]}>
              <Text style={styles.priceSummaryGrandTotal}>Grand Total:</Text>
              <Text
                style={[
                  styles.priceSummaryValue,
                  styles.priceSummaryGrandTotal,
                ]}
              >
                {formatMoney(
                  salesOrder.grandTotal ||
                    (salesOrder.salesOrderGeneralProductVariants?.reduce(
                      (sum, variant) =>
                        sum +
                        variant.quantity * variant.price -
                        variant.discount,
                      0
                    ) || 0) +
                      (salesOrder.salesOrderLensProducts?.reduce(
                        (sum, product) =>
                          sum + product.price - product.discount,
                        0
                      ) || 0)
                )}
              </Text>
            </View>
          </View>
        </View>

        {/* Note */}
        {isRetail && (
          <Text
            style={{
              fontFamily: "Helvetica-Oblique",
              textAlign: "center",
              marginTop: 10,
            }}
          >
            Terima kasih telah berbelanja di Optik 150K!{"\n"}
            Datang berkala untuk cek mata dan cuci kacamata anda di Optik 150K,
            {"\n"}
            Karena Semua, Berawal Dari Mata
          </Text>
        )}
      </Page>
    </Document>
  );
};

SalesOrderPdf.propTypes = {
  salesOrder: PropTypes.object.isRequired,
};
